import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
	ArrowRight,
	FlaskConical,
	Loader2,
	Plus,
	ShieldAlert,
	Trash2,
} from 'lucide-react';
import { PageTitle } from '@/components/ui';
import { ConfirmActionModal } from '@/components/ui/ConfirmActionModal';
import {
	useGetSandboxCatalog,
	useGetSandboxScenarios,
} from '@/services/queries/sandbox';
import {
	useCreateSandboxScenario,
	useDeleteSandboxScenario,
} from '@/services/mutations/sandbox';
import { getBackendErrorMessage } from '@/utils/errorMessage';
import { sandboxInputClass } from './components/SandboxModal';
import { ENTITY_LABELS, ENTITY_ORDER, formatDateTime } from './sandboxLabels';

/**
 * Admin › Sandbox: lista de cenários de simulação. Cada cenário agrupa os
 * dados de teste (usuários, profissionais, assinaturas, sessões...) criados
 * pelos serviços reais, para inspecionar e apagar em bloco.
 */
const SandboxPage = () => {
	const {
		data: catalog,
		isLoading: isCatalogLoading,
		error: catalogError,
	} = useGetSandboxCatalog();
	const { data: scenarios, isLoading } = useGetSandboxScenarios({
		enabled: Boolean(catalog?.enabled),
	});
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [deleteTarget, setDeleteTarget] = useState<Sandbox.Scenario | null>(
		null,
	);

	const { mutate: createScenario, isPending: isCreating } =
		useCreateSandboxScenario(() => {
			setName('');
			setDescription('');
		});
	const { mutate: deleteScenario, isPending: isDeleting } =
		useDeleteSandboxScenario(() => setDeleteTarget(null));

	const disabledMessage = catalogError
		? getBackendErrorMessage(catalogError) ??
		  'Não foi possível carregar o sandbox.'
		: null;

	return (
		<section className="space-y-6 px-8 py-8">
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div className="space-y-1">
					<div className="flex items-center gap-3">
						<span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-stone-900 text-white">
							<FlaskConical className="h-5 w-5" />
						</span>
						<PageTitle text="Sandbox" />
					</div>
					<p className="max-w-3xl text-sm text-stone-400">
						Simule a plataforma de ponta a ponta: crie contas de
						usuário e profissional, ative assinaturas, agende,
						pague, inicie e finalize sessões, responda o match e
						monte empresas. Tudo passa pelas regras reais do backend
						e fica agrupado em um cenário que você apaga quando
						quiser.
					</p>
				</div>
				{catalog ? (
					<div className="flex flex-wrap items-center gap-2">
						<span
							className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${
								catalog.enabled
									? 'bg-emerald-50 text-emerald-700'
									: 'bg-rose-50 text-rose-700'
							}`}
						>
							<span
								className={`h-2 w-2 rounded-full ${
									catalog.enabled
										? 'bg-emerald-500'
										: 'bg-rose-500'
								}`}
							/>
							{catalog.enabled
								? 'Sandbox ligado'
								: 'Sandbox desligado'}
						</span>
						{catalog.is_production ? (
							<span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800">
								<ShieldAlert className="h-3.5 w-3.5" />
								ambiente de produção
							</span>
						) : null}
					</div>
				) : null}
			</div>

			{disabledMessage ? (
				<div className="rounded-[2rem] border border-dashed border-rose-200 bg-rose-50/40 p-10 text-center">
					<h3 className="text-base font-black text-stone-900">
						Sandbox indisponível
					</h3>
					<p className="mx-auto mt-2 max-w-lg text-sm text-stone-500">
						{disabledMessage}
					</p>
				</div>
			) : null}

			{catalog?.enabled ? (
				<>
					<div className="rounded-2xl border border-stone-100 bg-white p-5 shadow-sm">
						<p className="text-[11px] font-black uppercase tracking-wide text-stone-400">
							Novo cenário
						</p>
						<form
							className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto]"
							onSubmit={(event) => {
								event.preventDefault();
								if (!name.trim()) return;
								createScenario({
									name: name.trim(),
									description:
										description.trim() || undefined,
								});
							}}
						>
							<input
								className={sandboxInputClass}
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="Nome (ex.: Jornada B2C com plano)"
								maxLength={120}
							/>
							<input
								className={sandboxInputClass}
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								placeholder="O que você quer testar (opcional)"
								maxLength={500}
							/>
							<button
								type="submit"
								disabled={isCreating || !name.trim()}
								className="inline-flex items-center justify-center gap-2 rounded-full bg-stone-900 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-stone-800 disabled:opacity-60"
							>
								<span className="flex h-4 w-4 items-center justify-center">
									{isCreating ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<Plus className="h-4 w-4" />
									)}
								</span>
								<span>Criar cenário</span>
							</button>
						</form>
						<p className="mt-3 text-xs text-stone-400">
							E-mails de teste usam o domínio{' '}
							<code className="rounded bg-stone-100 px-1.5 py-0.5 text-stone-600">
								@{catalog.email_domain}
							</code>{' '}
							e a senha padrão é{' '}
							<code className="rounded bg-stone-100 px-1.5 py-0.5 text-stone-600">
								{catalog.default_password}
							</code>
							.
						</p>
					</div>

					{isLoading || isCatalogLoading ? (
						<p className="text-sm text-stone-400">
							Carregando cenários...
						</p>
					) : (scenarios ?? []).length === 0 ? (
						<div className="rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 p-10 text-center">
							<h3 className="text-base font-black text-stone-900">
								Nenhum cenário ainda
							</h3>
							<p className="mx-auto mt-2 max-w-md text-sm text-stone-400">
								Crie o primeiro cenário acima. Dentro dele você
								roda uma jornada guiada em um clique ou monta
								cada passo na mão.
							</p>
						</div>
					) : (
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
							{(scenarios ?? []).map((scenario) => {
								const total = ENTITY_ORDER.reduce(
									(acc, type) => acc + scenario.counts[type],
									0,
								);
								return (
									<div
										key={scenario.id}
										className="group flex flex-col gap-4 rounded-2xl border border-stone-100 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
									>
										<div className="flex items-start justify-between gap-2">
											<div className="min-w-0">
												<h3 className="truncate text-base font-black text-stone-900">
													{scenario.name}
												</h3>
												<p className="text-[11px] text-stone-400">
													criado em{' '}
													{formatDateTime(
														scenario.created_at,
													)}
												</p>
											</div>
											<button
												type="button"
												onClick={() =>
													setDeleteTarget(scenario)
												}
												className="rounded-lg p-2 text-stone-400 hover:bg-rose-50 hover:text-rose-600"
												title="Apagar cenário e seus dados"
											>
												<Trash2 size={16} />
											</button>
										</div>
										{scenario.description ? (
											<p className="line-clamp-2 text-xs text-stone-500">
												{scenario.description}
											</p>
										) : null}
										<div className="flex flex-wrap gap-1.5">
											{total === 0 ? (
												<span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold text-stone-500">
													vazio
												</span>
											) : (
												ENTITY_ORDER.filter(
													(type) =>
														scenario.counts[type] >
														0,
												).map((type) => (
													<span
														key={type}
														className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold text-stone-600"
													>
														{scenario.counts[type]}{' '}
														{ENTITY_LABELS[
															type
														].toLowerCase()}
													</span>
												))
											)}
										</div>
										{scenario.last_event ? (
											<p className="line-clamp-2 text-[11px] text-stone-400">
												Último evento:{' '}
												{scenario.last_event.summary}
											</p>
										) : null}
										<Link
											to={`/admin/sandbox/${scenario.id}`}
											className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-stone-900 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-stone-800"
										>
											Abrir cenário
											<ArrowRight className="h-3.5 w-3.5" />
										</Link>
									</div>
								);
							})}
						</div>
					)}
				</>
			) : null}

			<ConfirmActionModal
				isOpen={Boolean(deleteTarget)}
				onClose={() => (isDeleting ? undefined : setDeleteTarget(null))}
				onConfirm={() =>
					deleteTarget && deleteScenario(deleteTarget.id)
				}
				title="Apagar cenário?"
				description={
					<>
						Isso apaga <strong>{deleteTarget?.name}</strong> e TODOS
						os dados de teste criados por ele: usuários,
						profissionais, assinaturas, sessões, empresas e
						respostas. Não afeta nada fora do cenário.
					</>
				}
				confirmButtonText={isDeleting ? 'Apagando...' : 'Apagar tudo'}
				disableConfirm={isDeleting}
			/>
		</section>
	);
};

export { SandboxPage };
